import React, { Dispatch, SetStateAction, useState, useEffect } from "react";
import InsightFiltersViewModel from "../../ViewModels/InsightFiltersViewModel";
import moment, { Moment } from "moment";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import Select from "@mui/material/Select";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import DialogContent from "@mui/material/DialogContent";
import MenuItem from "@mui/material/MenuItem";
import WeekPicker from "./CustomDatePickers/WeekPicker";
import MonthPicker from "./CustomDatePickers/MonthPicker";
import DayPicker from "./CustomDatePickers/DayPicker";
import ResqWeekPicker from "./CustomDatePickers/ResqWeekPicker";
import { InsightView } from "../../../ViewModels/InsightView";

interface IDateFilterDialogProps {
    view: InsightView
    open: boolean
    setOpen: (value: boolean) => void
    filters: InsightFiltersViewModel
    setFilters: Dispatch<SetStateAction<InsightFiltersViewModel>>
}

export default function DateFilterDialog({ view, open, setOpen, filters, setFilters }: IDateFilterDialogProps) {
    const [datePreset, setDatePreset] = useState(filters.datePreset);
    const [startDate, setStartDate] = useState<Moment>(moment(filters.startDate));
    const [endDate, setEndDate] = useState<Moment>(moment(filters.endDate));
    const usingResqWeek = (view === InsightView.Agent || view === InsightView.Operational);

    useEffect(() => {
        setDatePreset(filters.datePreset)
    }, [filters.datePreset])

    useEffect(() => {
        const today = moment();
        onDateChange(today);
    }, [datePreset])

    const confirm = () => {
        setOpen(false);

        // Converting GMT to UTC
        let startDateFormatted = new Date(startDate.toLocaleString());
        startDateFormatted = addHours(startDateFormatted, 1);

        let endDateFormatted = new Date(endDate.toLocaleString());
        endDateFormatted = addHours(endDateFormatted, 1);

        setFilters(prevState => ({
            ...prevState,
            datePreset: datePreset,
            startDate: startDateFormatted,
            endDate: endDateFormatted
        }));
    }

    const addHours = (date, hours) => {
        const hoursToAdd = hours * 60 * 60 * 1000;
        date.setTime(date.getTime() + hoursToAdd);
        return date;
    }

    const onDatePresetChange = (event: any) => {
        setDatePreset(event.target.value);
    }

    const onDateChange = (value: Moment | null) => {
        if (value) {
            if (datePreset === 'Custom Day') {
                const startDate = value.clone().set({ hour: 0, minute: 0, second: 0 })
                setStartDate(startDate);
                const endDate = value.clone().set({ hour: 23, minute: 59, second: 59 })
                setEndDate(endDate);
            }
            if (datePreset === 'Custom Week') {
                let start = value.clone().startOf('week');
                let end = value.clone().endOf('week');

                if (usingResqWeek) {
                    //resq week is from sat of the previous week to fri of the current week
                    start = start.subtract(2, 'd'); //stop on saterday
                    end = end.subtract(3, 'd'); //stop on friday
                }

                setStartDate(start);
                setEndDate(end);
            }
            if (datePreset === 'Custom Month') {
                const start = value.clone().startOf('month');
                const end = value.clone().endOf('month');
                setStartDate(start);
                setEndDate(end);
            }
        }
    }

    const options = [
        'Yesterday',
        'This Week',
        'Last Week',
        'This Month',
        'Last Month',
        'Custom Day',
        'Custom Week',
        'Custom Month'
    ];

    return (
        <>
            <Dialog open={open} fullWidth maxWidth='sm'>
                <DialogTitle>Date Filter</DialogTitle>
                <DialogContent>
                    <Grid container spacing={1}>
                        <Grid size={12}>
                            <Select
                                value={datePreset}
                                onChange={onDatePresetChange}
                                fullWidth>
                                {options.map(option =>
                                    <MenuItem key={option} value={option}>{option}</MenuItem>)}
                            </Select>
                        </Grid>
                        {datePreset === 'Custom Day' &&
                            <Grid size={12}>
                                <DayPicker onDateChange={onDateChange} value={startDate} />
                            </Grid>
                        }
                        {datePreset === 'Custom Week' &&
                            <Grid size={12}>
                                {usingResqWeek ?
                                    <ResqWeekPicker onDateChange={onDateChange} startOfWeek={startDate} />
                                    :
                                    <WeekPicker onDateChange={onDateChange} startOfWeek={startDate} />
                                }
                            </Grid>
                        }
                        {datePreset === 'Custom Month' &&
                            <Grid size={12}>
                                <MonthPicker onDateChange={onDateChange} value={startDate} />
                            </Grid>
                        }
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)}>
                        Cancel
                    </Button>
                    <Button onClick={confirm}>
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};
